import { useState, useRef } from "react";
import { Save, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AllergenSelector from "@/components/AllergenSelector";
import OptionsSelector from "@/components/OptionsSelector";

export default function ProductForm({ 
  productData, 
  onProductChange, 
  onSave, 
  onCancel, 
  onImageUpload, 
  uploadingImage,
  saving = false
}) {
  const [activeTab, setActiveTab] = useState('info');
  const fileInputRef = useRef(null);

  return (
    <div className="mb-4 p-6 bg-gray-50 rounded-lg">
      <h4 className="font-semibold mb-4 text-gray-900 text-lg">Nuevo Producto</h4>
      <div className="space-y-4">
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 font-medium transition ${activeTab === 'info' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Info
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('image')}
            className={`px-4 py-2 font-medium transition ${activeTab === 'image' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Foto
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('allergens')}
            className={`px-4 py-2 font-medium transition ${activeTab === 'allergens' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Alergenos
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('options')}
            className={`px-4 py-2 font-medium transition ${activeTab === 'options' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Opciones
          </button>
        </div>

        <div className="mt-4">
          {activeTab === 'info' && (
            <div className="space-y-3">
              <Input
                placeholder="Nombre del producto"
                value={productData.name}
                onChange={(e) => onProductChange({ ...productData, name: e.target.value })}
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Precio (euro)"
                value={productData.price}
                onChange={(e) => onProductChange({ ...productData, price: e.target.value })}
              />
              <Textarea
                placeholder="Ingredientes / Descripcion"
                value={productData.description}
                onChange={(e) => onProductChange({ ...productData, description: e.target.value })}
                rows={4}
              />
            </div>
          )}

          {activeTab === 'image' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => onImageUpload(e.target.files[0])}
                  className="hidden"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  {uploadingImage ? "Subiendo..." : "Subir Imagen"}
                </Button>
                {productData.image_url && (
                  <div className="mt-4">
                    <img src={productData.image_url} alt="Preview" className="max-w-xs mx-auto rounded-lg" />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'allergens' && (
            <AllergenSelector
              selectedAllergens={productData.allergens}
              selectedTraces={productData.traces}
              onChange={({ allergens, traces }) => 
                onProductChange({ ...productData, allergens, traces })
              }
            />
          )}

          {activeTab === 'options' && (
            <OptionsSelector
              selectedOptions={productData.options}
              onChange={(options) => onProductChange({ ...productData, options })}
            />
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-6">
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Guardar Producto
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={saving}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}
